import { type ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ThemeProvider } from '@m/alchemy-ui/theme';
import { IntlProvider, type SupportedLocales } from '../../context';
import { ErrorFallback } from '../../common/components';
import { type CommunityDesign } from '../../common/interfaces';
import { getCommunityTheme } from '../../common/theme';

interface LoginWrapperProps {
  readonly locale: SupportedLocales;
  readonly communityColors?: CommunityDesign;
  readonly children: ReactNode;
}

/**
 * Lightweight wrapper for the login page.
 *
 * Provides ErrorBoundary + IntlProvider (for translations) + ThemeProvider
 * (for alchemy-ui components). Builds a community-aware theme from the
 * server-provided colours so that buttons, links, etc. use the
 * community's dynamic palette.
 *
 * Does NOT include Apollo, CommunityProvider, UserProvider, etc.
 * If you later need those, add the relevant provider here rather than
 * switching back to the full Wrapper.
 */
export const LoginWrapper = ({ locale, communityColors, children }: LoginWrapperProps) => (
  <ErrorBoundary FallbackComponent={ErrorFallback} onError={console.error}>
    <ThemeProvider theme={getCommunityTheme({ communityColors })}>
      <IntlProvider locale={locale || 'en-GB'}>{children}</IntlProvider>
    </ThemeProvider>
  </ErrorBoundary>
);
